import React, { useState } from "react";
import styles from "./PurchaseOrderDashboard.module.scss";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Space,
  Spin,
  Button
} from "antd";
import { 
  WarningOutlined,
  DollarOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  PieChartOutlined,
  ExclamationCircleOutlined,
  BellOutlined
} from "@ant-design/icons";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Legend,
  Tooltip
);

const PurchaseOrderDashboard = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleUpdate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const purchaseAlerts = [
    {
      id: 1,
      type: "stock_break",
      title: "Quiebre de stock inminente en stock central - 8 productos",
      severity: "error"
    },
    {
      id: 2,
      type: "delayed_order",
      title: "3 órdenes de compra pasadas de la fecha estimada de entrega",
      severity: "error"
    },
    {
      id: 3,
      type: "draft_pending",
      title: "5 borradores de órdenes de compra pendientes de confirmar",
      severity: "warning"
    },
    {
      id: 4,
      type: "low_stock",
      title: "Stock bajo en categoría Deportivos",
      severity: "warning"
    }
  ];

  const kpis = {
    productosQuiebreProximo: 15,
    productosSobrestock: 12,
    optimizacionCatalogo: 0,
    optimizacionInventario: 0,
    rotacion: 0
  };

  const inventarioValorizadoData = {
    labels: ["Deportivos", "Casuales", "Formales", "Accesorios", "Calzado", "Ropa", "Electrónica", "Hogar"],
    datasets: [
      {
        data: [350000, 280000, 220000, 150000, 120000, 100000, 80000, 50000],
        backgroundColor: [
          "#8b5cf6",
          "#1890ff",
          "#f5222d",
          "#fa8c16",
          "#52c41a",
          "#13c2c2",
          "#722ed1",
          "#eb2f96"
        ],
        borderWidth: 0,
      },
    ],
  };

  const ordenesCompraEstadoData = {
    labels: ["Borrador", "Enviada", "En Tránsito", "Completada", "Cancelada"],
    datasets: [
      {
        data: [8, 15, 12, 45, 3],
        backgroundColor: ["#f5222d", "#8b5cf6", "#1890ff", "#52c41a", "#fa8c16"],
        borderWidth: 0,
      },
    ],
  };

  const topProductosDemandaData = {
    labels: ["Producto A", "Producto B", "Producto C", "Producto D", "Producto E"],
    datasets: [
      {
        label: "Unidades vendidas",
        data: [850, 720, 650, 580, 520],
        backgroundColor: "#1890ff",
        borderRadius: 4,
      },
    ],
  };

  const distribucionVentasCategoriaData = {
    labels: [
      "Deportivos",
      "Casuales",
      "Formales",
      "Accesorios",
      "Calzado",
      "Ropa",
      "Electrónica",
      "Hogar",
      "Belleza",
      "Otros"
    ],
    datasets: [
      {
        label: "% de venta",
        data: [28, 20, 16, 12, 10, 7, 4, 2, 1, 0.5],
        backgroundColor: [
          "#8b5cf6",
          "#1890ff",
          "#f5222d",
          "#fa8c16",
          "#52c41a",
          "#13c2c2",
          "#722ed1",
          "#eb2f96",
          "#faad14",
          "#a0d911"
        ],
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      x: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
      y: {
        grid: {
          display: false
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
    },
  };

  return (
    <div className={styles.dashboardWrapper}>
      <div className={styles.dashboardHeader}>
        <div className={styles.headerContent}>
          <Space size="middle">
            <Button type="primary" onClick={handleUpdate} className={styles.updateButton}>
              Actualizar
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={18}>
          <div className={styles.kpisSection}>
            <Row gutter={[16, 16]} align="stretch">
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Productos con quiebre próximo"
                      value={formatNumber(kpis.productosQuiebreProximo)}
                      prefix={<WarningOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#f5222d' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Productos con sobrestock"
                      value={formatNumber(kpis.productosSobrestock)}
                      prefix={<ExclamationCircleOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#fa8c16' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Optimización de catálogo"
                      value={formatNumber(kpis.optimizacionCatalogo)}
                      prefix={<BarChartOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} align="stretch" style={{ marginTop: 16 }}>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Optimización de inventario"
                      value={formatNumber(kpis.optimizacionInventario)}
                      prefix={<PieChartOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Spin>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Card className={styles.kpiCard}>
                  <Spin spinning={isLoading}>
                    <Statistic
                      title="Rotación"
                      value={formatNumber(kpis.rotacion)}
                      prefix={<ShoppingOutlined className={styles.kpiIcon} />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Spin>
                </Card>
              </Col>
            </Row>
          </div>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <PieChartOutlined />
                    <span>Inventario total valorizado</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Doughnut data={inventarioValorizadoData} options={doughnutOptions} />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <PieChartOutlined />
                    <span>Órdenes de Compra por estado</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Doughnut data={ordenesCompraEstadoData} options={doughnutOptions} />
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className={styles.chartsSection}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Top productos por demanda total</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={topProductosDemandaData} options={chartOptions} />
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BarChartOutlined />
                    <span>Distribución de venta por categoría</span>
                  </Space>
                }
                className={styles.chartCard}
              >
                <div className={styles.chartContainer}>
                  <Bar data={distribucionVentasCategoriaData} options={horizontalBarOptions} />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={6}>
          <Card 
            title={
              <Space>
                <BellOutlined />
                <span>Alertas de compras</span>
              </Space>
            }
            className={styles.alertsCard}
          >
            <div className={styles.alertsList}>
              {purchaseAlerts.map((alert) => (
                <div key={alert.id} className={styles.alertItem}>
                  <span className={styles.alertArrow}>→</span>
                  <span className={styles.alertText}>{alert.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PurchaseOrderDashboard;